import React, { useState, useRef, useEffect } from 'react';
import CrmSearchInput from './CrmSearchInput';

// Shared CRM dropdown component and styles, based on the Tasks page design.
//
// Props (initial, single-select use case):
// - value: current value (string)
// - options: [{ value, label, color? }]
// - onChange: (newValue) => void
// - placeholder: string (shown when no value selected)
// - showDot: boolean (if true, render a colored dot for options with `color`)
// - minWidth / style overrides: optional layout tweaks
//
// This is intentionally simple for now and can be extended as we migrate
// more dropdowns to it.

const filterSelectStyle = {
  width: '100%',
  backgroundColor: 'var(--sl-bg-primary, #FFFFFF)',
  border: '1px solid var(--sl-border-primary, #D5D7DA)',
  borderRadius: 8,
  padding: '8px 32px 8px 12px',
  boxShadow: '0 1px 2px rgba(10,13,18,0.05)',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  fontSize: 14,
  lineHeight: '20px',
};

const filterSelectChevronStyle = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: 'var(--sl-text-quaternary, #717680)',
  fontSize: 12,
};

const statusMenuStyle = {
  marginTop: 4,
  minWidth: '100%',
  border: '1px solid var(--sl-border-secondary, #E9EAEB)',
  borderRadius: 8,
  boxShadow:
    '0 12px 16px -4px rgba(10,13,18,0.08), 0 4px 6px -2px rgba(10,13,18,0.03), 0 2px 2px -1px rgba(10,13,18,0.04)',
  padding: '4px 0',
  backgroundColor: 'var(--sl-bg-primary, #FFFFFF)',
  zIndex: 1000,
};

const statusMenuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '6px 12px',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
};

const statusDotStyle = (color) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: color,
});

export const crmDropdownStyles = {
  filterSelectStyle,
  filterSelectChevronStyle,
  statusMenuStyle,
  statusMenuItemStyle,
  statusDotStyle,
};

function CrmDropdown({
  value,
  options,
  onChange,
  placeholder = 'Select',
  showDot = false,
  minWidth = 160,
  buttonStyle = {},
  menuAlign = 'left', // 'left' | 'right'
  ariaLabel,
  enableSearch = true,
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  const safeOptions = Array.isArray(options) ? options : [];
  const selected = safeOptions.find((opt) => opt.value === value) || null;

  useEffect(() => {
    if (!open) return;

    const handleClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [open]);

  useEffect(() => {
    if (open && enableSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, enableSearch]);

  const handleSelect = (newValue) => {
    if (onChange) onChange(newValue);
    setOpen(false);
  };

  const handleToggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!next) {
        setSearchValue('');
      }
      return next;
    });
  };

  const label = selected ? selected.label : placeholder;

  const filteredOptions = safeOptions.filter((opt) => {
    if (!enableSearch || !searchValue) return true;
    const optionLabel = (opt && opt.label) || '';
    const optionSubtitle = (opt && opt.subtitle) || '';
    const searchLower = searchValue.toLowerCase();
    return optionLabel.toLowerCase().includes(searchLower) ||
      optionSubtitle.toLowerCase().includes(searchLower);
  });

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', minWidth }}
    >
      <button
        type="button"
        style={{
          ...filterSelectStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: 32,
          position: 'relative',
          ...buttonStyle,
        }}
        onClick={handleToggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || placeholder}
      >
        <span className="d-inline-flex align-items-center">
          {showDot && selected && selected.color && (
            <span
              style={{
                ...statusDotStyle(selected.color),
                marginRight: 8,
              }}
            />
          )}
          <span className="figma-text sm">{label}</span>
        </span>
        <i
          className="fal fa-chevron-down"
          style={filterSelectChevronStyle}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="dropdown-menu show"
          style={{
            ...statusMenuStyle,
            [menuAlign === 'right' ? 'right' : 'left']: 0,
          }}
          role="listbox"
        >
          {enableSearch && (
            <div style={{ padding: '4px 8px 8px' }}>
              <CrmSearchInput
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder=""
                ariaLabel={`Search ${placeholder.toLowerCase()}`}
                inputRef={searchInputRef}
                useDebounce={false}
                shortcutLabel={null}
                wrapperStyle={{ width: '100%' }}
              />
            </div>
          )}

          {filteredOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              style={statusMenuItemStyle}
              onClick={() => handleSelect(opt.value)}
            >
              {showDot && opt.color && (
                <span
                  style={{
                    ...statusDotStyle(opt.color),
                    marginRight: 8,
                  }}
                />
              )}
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span className="figma-text sm">{opt.label}</span>
                {opt.subtitle && (
                  <span style={{ fontSize: 12, color: 'var(--sl-text-quaternary, #717680)', marginTop: 2 }}>
                    {opt.subtitle}
                  </span>
                )}
              </span>
            </button>
          ))}

          {filteredOptions.length === 0 && (
            <div className="text-muted small px-3 py-2">
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CrmDropdown;
